import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Train, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DestinationAutocomplete from '../components/DestinationAutocomplete';
import BookingConfirmation from '../components/BookingConfirmation';
import BookingModal from '../components/BookingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainsPage() {
  const [searchData, setSearchData] = useState({
    from_station: '',
    to_station: '',
    travel_date: '',
    passengers: 1
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedTrain, setSelectedTrain] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

 const stationCodes = {
  hyderabad: "HYD",
  mumbai: "CSMT",
  delhi: "NDLS",
  bangalore: "SBC",
  chennai: "MAS",
  kolkata: "HWH"
};

const handleSearch = (e) => {
  e.preventDefault();

  if (!searchData.from_station || !searchData.to_station || !searchData.travel_date) {
    toast.error("Please fill all required fields");
    return;
  }

  const from = stationCodes[searchData.from_station.toLowerCase()] || "HYD";
  const to = stationCodes[searchData.to_station.toLowerCase()] || "CSMT";

  const date = searchData.travel_date;

  const redirectUrl =
    `https://www.confirmtkt.com/rbooking-d/trains/list?from=${from}&to=${to}&date=${date}`;

  window.open(redirectUrl, "_blank");
};



  const openBookingModal = (train) => {
    setSelectedTrain(train);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (customerData) => {
    if (!selectedTrain) return;

    try {
      setBookingLoading(true);
      const res = await axios.post(`${API}/bookings/create`, {
        booking_type: 'train',
        customer_name: customerData.name,
        customer_mobile: customerData.mobile,
        customer_email: customerData.email || '',
        from_location: selectedTrain.from_station,
        to_location: selectedTrain.to_station,
        travel_date: searchData.travel_date,
        travelers: searchData.passengers,
        provider_name: `${selectedTrain.train_name} (${selectedTrain.train_number})`,
        departure_time: selectedTrain.departure_time,
        arrival_time: selectedTrain.arrival_time,
        duration: selectedTrain.duration,
        class_type: selectedTrain.class,
        price: selectedTrain.price * searchData.passengers
      });
      
      setShowBookingModal(false);
      setBookingDetails({
        bookingId: res.data.booking_id,
        name: `${selectedTrain.train_name} (${selectedTrain.train_number})`,
        details: `${selectedTrain.from_station} → ${selectedTrain.to_station} • ${selectedTrain.class}`,
        date: searchData.travel_date,
        travelers: searchData.passengers,
        price: selectedTrain.price * searchData.passengers
      });
      setShowConfirmation(true);
    } catch (err) {
      toast.error('Error creating booking');
    } finally {
      setBookingLoading(false);
    }
  };

  const getAvailabilityColor = (availability) => {
    if (availability === 'Available') return 'text-green-600 bg-green-50';
    if (availability === 'RAC') return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="trains-page">
      {/* Hero */}
      <section className="relative h-64 md:h-80 bg-gradient-to-r from-orange-500 to-orange-700 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=1920"
            alt="Trains"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-4"
          >
            <Train className="w-12 h-12 text-white" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-medium text-white mb-4"
          >
            Book Trains
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-orange-100"
          >
            Travel across India with Indian Railways
          </motion.p>
        </div>
      </section>

      {/* Search Form */}
      <section className="max-w-4xl mx-auto px-6 -mt-12 relative z-20">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSearch}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
          data-testid="train-search-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <DestinationAutocomplete
              label="From Station"
              value={searchData.from_station}
              onChange={(val) => setSearchData({ ...searchData, from_station: val })}
              placeholder="New Delhi"
            />
            <DestinationAutocomplete
              label="To Station"
              value={searchData.to_station}
              onChange={(val) => setSearchData({ ...searchData, to_station: val })}
              placeholder="Mumbai Central"
            />
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Travel Date</label>
              <input
                type="date"
                value={searchData.travel_date}
                onChange={(e) => setSearchData({ ...searchData, travel_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
                data-testid="travel-date"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Passengers</label>
              <select
                value={searchData.passengers}
                onChange={(e) => setSearchData({ ...searchData, passengers: parseInt(e.target.value) })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none bg-white"
                data-testid="passengers"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-12 py-6 text-lg font-medium"
              data-testid="search-trains-btn"
            >
              {loading ? 'Searching...' : 'Search Trains'}
            </Button>
          </div>
        </motion.form>
      </section>

      {/* Discount Banner */}
      <section className="max-w-4xl mx-auto px-6 mt-8">
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-orange-600 flex-shrink-0" />
          <p className="text-sm text-orange-800">
            <span className="font-semibold">Exclusive Travel Discounts!</span> Book through ID4U Travels for special offers on train tickets.
          </p>
        </div>
      </section>

      {/* Results */}
      {searched && (
        <section className="max-w-4xl mx-auto px-6 py-12" data-testid="train-results">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">
            {results.length > 0 ? `${results.length} Trains Found` : 'No Trains Found'}
          </h2>
          <div className="space-y-4">
            {results.map((train, index) => (
              <motion.div
                key={train.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-slate-100"
                data-testid={`train-result-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <Train className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{train.train_name}</h3>
                      <p className="text-sm text-slate-500">#{train.train_number} • {train.class}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{train.departure_time}</p>
                      <p className="text-sm text-slate-500">{train.from_station}</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <p className="text-xs text-slate-400 mb-1">{train.duration}</p>
                      <div className="w-20 h-px bg-slate-300 relative">
                        <Train className="w-4 h-4 text-orange-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{train.arrival_time}</p>
                      <p className="text-sm text-slate-500">{train.to_station}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-600">₹{train.price?.toLocaleString()}</p>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getAvailabilityColor(train.availability)}`}>
                        {train.availability}
                      </span>
                    </div>
                    <Button
                      onClick={() => openBookingModal(train)}
                      className="bg-orange-600 hover:bg-orange-700 text-white rounded-full"
                      data-testid={`book-train-${index}`}
                    >
                      Book Now
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <BookingModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            onSubmit={handleBookingSubmit}
            title="Book Your Train Ticket"
            loading={bookingLoading}
          />
        )}
      </AnimatePresence>

      {/* Booking Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <BookingConfirmation
            bookingType="train"
            bookingDetails={bookingDetails}
            onClose={() => setShowConfirmation(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
